/**
 * "Limits & alerts" — Dashboard section (ADR-0074, reconciled to ADR-0543).
 *
 * Reconciled from apps/web's `features/limits` — ONE page at /limits with three
 * tabs (Budget / Alerts / Traffic), which replaced the separate spend-caps,
 * rate-limits and usage-alarms surfaces this page used to describe. Caps and
 * rate limits enforce (block); alarms notify.
 *
 * The section headings stay named for the CONCEPTS rather than the tabs, and
 * their anchor ids are unchanged: a reader arrives here searching "spend caps",
 * not "budget tab", and `#spend` / `#rate` / `#alarms` are live deep links.
 * Each body names the tab that now holds the control.
 */
import type { DocPageContent } from '../types';

export const DASHBOARD_LIMITS_PAGE: DocPageContent = {
  slug: 'dashboard/limits',
  titleKey: 'docs.dashboard.limits.title',
  descriptionKey: 'docs.dashboard.limits.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.limits.lead' },

    // Ahead of the three sections, not after them: everything below describes
    // per-scope controls that are ABSENT in Simple mode (ui-design.md §20), so a
    // reader who meets this callout last has already been told to look for
    // fields their page is not showing them.
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.dashboard.limits.modes.title',
      textKey: 'docs.dashboard.limits.modes.body',
    },

    { kind: 'heading', level: 2, id: 'spend', textKey: 'docs.dashboard.limits.spend.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.limits.spend.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.limits.spend.item.scope',
        'docs.dashboard.limits.spend.item.hardcap',
        'docs.dashboard.limits.spend.item.thresholds',
      ],
    },

    { kind: 'heading', level: 2, id: 'rate', textKey: 'docs.dashboard.limits.rate.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.limits.rate.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.limits.rate.item.scope',
        'docs.dashboard.limits.rate.item.softhard',
        'docs.dashboard.limits.rate.item.window',
      ],
    },

    { kind: 'heading', level: 2, id: 'alarms', textKey: 'docs.dashboard.limits.alarms.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.limits.alarms.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.limits.alarms.item.threshold',
        'docs.dashboard.limits.alarms.item.channels',
        'docs.dashboard.limits.alarms.item.defaults',
      ],
    },

    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.dashboard.limits.enforce.title',
      textKey: 'docs.dashboard.limits.enforce.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.limits.next.limits.title',
          textKey: 'docs.dashboard.limits.next.limits.text',
          to: { docSlug: 'billing/limits' },
        },
        {
          titleKey: 'docs.dashboard.limits.next.usage.title',
          textKey: 'docs.dashboard.limits.next.usage.text',
          to: { docSlug: 'billing/usage' },
        },
      ],
    },
  ],
};
